import { useEffect, useRef } from "react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const cache = {}; // `${lang}::${trimmedText}` -> translated
const origText = new WeakMap();
const origPh = new WeakMap();
const trackedNodes = new Set();
const trackedPh = new Set();

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA"]);
const hasLetter = (s) => /\p{L}/u.test(s);

function shouldSkip(node) {
  let el = node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.getAttribute && (el.getAttribute("translate") === "no" || el.hasAttribute("data-no-translate"))) return true;
    el = el.parentElement;
  }
  return false;
}

function collectTextNodes() {
  const out = [];
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const v = n.nodeValue;
      if (!v || !v.trim() || !hasLetter(v)) return NodeFilter.FILTER_REJECT;
      if (shouldSkip(n)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node;
  while ((node = walker.nextNode())) out.push(node);
  return out;
}

export default function Translator() {
  const { lang, setTranslating } = useLang();
  const langRef = useRef(lang);
  const observerRef = useRef(null);
  const runningRef = useRef(false);
  const rerunRef = useRef(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    langRef.current = lang;
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }

    if (lang === "en") { restore(); return; }

    let cancelled = false;
    (async () => {
      await translateAll();
      if (!cancelled) startObserver();
    })();
    return () => { cancelled = true; if (observerRef.current) observerRef.current.disconnect(); };
    // eslint-disable-next-line
  }, [lang]);

  function restore() {
    trackedNodes.forEach((n) => { const o = origText.get(n); if (o != null && n.nodeValue !== o) n.nodeValue = o; });
    trackedPh.forEach((el) => { const o = origPh.get(el); if (o != null) el.setAttribute("placeholder", o); });
  }

  function apply(lg) {
    trackedNodes.forEach((n) => {
      const orig = origText.get(n); if (orig == null) return;
      const t = orig.trim(); const tr = cache[`${lg}::${t}`];
      const target = tr !== undefined ? orig.replace(t, tr) : orig;
      if (n.nodeValue !== target) n.nodeValue = target;
    });
    trackedPh.forEach((el) => {
      const orig = origPh.get(el) || ""; const t = orig.trim(); const tr = cache[`${lg}::${t}`];
      const target = tr !== undefined ? tr : orig;
      if (el.getAttribute("placeholder") !== target) el.setAttribute("placeholder", target);
    });
  }

  async function translateAll() {
    const lg = langRef.current;
    if (lg === "en") return;
    if (runningRef.current) { rerunRef.current = true; return; }
    runningRef.current = true;
    if (observerRef.current) observerRef.current.disconnect();

    const pending = new Set();
    collectTextNodes().forEach((n) => {
      if (!origText.has(n)) origText.set(n, n.nodeValue);
      trackedNodes.add(n);
      const t = origText.get(n).trim();
      if (t && hasLetter(t) && cache[`${lg}::${t}`] === undefined) pending.add(t);
    });
    document.querySelectorAll("[placeholder]").forEach((el) => {
      if (!origPh.has(el)) origPh.set(el, el.getAttribute("placeholder"));
      trackedPh.add(el);
      const t = (origPh.get(el) || "").trim();
      if (t && hasLetter(t) && cache[`${lg}::${t}`] === undefined) pending.add(t);
    });

    const list = [...pending];
    const CHUNK = 40;
    if (list.length) setTranslating(true);
    for (let i = 0; i < list.length; i += CHUNK) {
      const chunk = list.slice(i, i + CHUNK);
      try {
        const { data } = await api.post("/translate", { texts: chunk, target: lg });
        (data.translations || []).forEach((tr, j) => { cache[`${lg}::${chunk[j]}`] = tr; });
      } catch {
        // don't cache failures — allow a later retry to succeed
      }
      if (langRef.current !== lg) break; // language changed mid-flight
    }
    setTranslating(false);

    apply(langRef.current);
    if (observerRef.current === null && langRef.current !== "en") startObserver();
    else if (observerRef.current) observerRef.current.observe(document.body, { childList: true, subtree: true, characterData: true });

    runningRef.current = false;
    if (rerunRef.current) { rerunRef.current = false; translateAll(); }
  }

  function startObserver() {
    if (observerRef.current) observerRef.current.disconnect();
    const obs = new MutationObserver(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => translateAll(), 200);
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    observerRef.current = obs;
  }

  return null;
}
